import { Badge, Button, Group, Paper, SegmentedControl, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../api';
import { SupplierSectionNav } from '../components/supplier-section-nav';
import { apiHook } from '../treaty';

type Bucket = 'open' | 'overdue' | 'today' | 'upcoming' | 'completed' | 'cancelled';

export function SupplierFollowUpsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [bucket, setBucket] = useState<Bucket>('open');
  const query = apiHook.useQuery((client) =>
    client['supplier-follow-ups'].get({ query: { bucket } }),
  );
  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['supplier-follow-ups'] }),
      queryClient.invalidateQueries({ queryKey: ['suppliers'] }),
    ]);
  }
  async function complete(id: string) {
    const response = await api.api.api.v1['supplier-follow-ups']({ id }).complete.post({});
    if (response.error)
      notifications.show({ color: 'red', message: 'Follow-up could not be completed.' });
    else {
      await refresh();
      notifications.show({ color: 'green', message: 'Follow-up completed and added to activity.' });
    }
  }
  async function cancel(id: string) {
    const response = await api.api.api.v1['supplier-follow-ups']({ id }).cancel.post();
    if (!response.error) await refresh();
  }
  return (
    <Stack gap='lg' maw={1100} mx='auto'>
      <div>
        <Title order={1}>Supplier follow-ups</Title>
        <Text c='dimmed' mt={5}>
          A shared action list for the whole team.
        </Text>
      </div>
      <SupplierSectionNav />
      <SegmentedControl
        fullWidth
        data={[
          ['open', 'All open'],
          ['overdue', 'Overdue'],
          ['today', 'Today'],
          ['upcoming', 'Upcoming'],
          ['completed', 'Completed'],
          ['cancelled', 'Cancelled'],
        ].map(([value, label]) => ({ value, label }))}
        value={bucket}
        onChange={(value) => setBucket(value as Bucket)}
      />
      <Stack gap='sm'>
        {query.isLoading && <Text c='dimmed'>Loading follow-ups…</Text>}
        {!query.isLoading && !query.data?.length && (
          <Paper withBorder radius='lg' p='xl'>
            <Text c='dimmed' ta='center'>
              No follow-ups in this view.
            </Text>
          </Paper>
        )}
        {query.data?.map((followUp) => (
          <Paper key={followUp.id} withBorder radius='lg' p='md'>
            <Group justify='space-between' align='start'>
              <div>
                <Group gap='xs'>
                  <Text fw={650}>{followUp.supplier.name}</Text>
                  <Badge color={followUp.activityType.color} variant='light'>
                    {followUp.activityType.name}
                  </Badge>
                </Group>
                <Text size='sm' mt={4}>
                  {followUp.instructions || 'No instructions'}
                </Text>
                <Text size='xs' c='dimmed' mt={4}>
                  Due {dayjs(followUp.dueAt).format('DD MMM YYYY, HH:mm')}
                  {followUp.contact ? ` · ${followUp.contact.name}` : ''}
                </Text>
              </div>
              <Group>
                <Button
                  size='xs'
                  variant='light'
                  onClick={() => void navigate(`/suppliers/${followUp.supplier.id}`)}
                >
                  Open supplier
                </Button>
                {!followUp.completedAt && !followUp.cancelledAt && (
                  <>
                    <Button size='xs' onClick={() => void complete(followUp.id)}>
                      Complete
                    </Button>
                    <Button
                      size='xs'
                      color='gray'
                      variant='subtle'
                      onClick={() => void cancel(followUp.id)}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Group>
            </Group>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}
